<template>
    <div :class="wrapperClasses">
        <button
            :class="buttonClasses"
            v-if="
                isBuyflow === true ||
                stage === 'start' ||
                (stage === 'chat' && chatService.isLoading)
            "
            @click="selectStage('select')"
        >
            <svg
                viewBox="0 0 100 100"
                focusable="false"
                role="image"
                aria-hidden="true"
                :class="chatService.isLoading ? 'animate-spin' : ''"
            >
                <path
                    d="M25,54.1677083 L75,54.1677083 L75,50.0005208 L25,50.0005208 L25,54.1677083 Z M25,37.5005208 L75,37.5005208 L75,33.3333333 L25,33.3333333 L25,37.5005208 Z M42.03125,95.8333333 L37.5,95.8333333 L37.5,91.6661458 L42.03125,91.6661458 C48.721875,91.6661458 54.7375,87.9489583 57.7296875,81.9645833 L60.6359375,76.1520833 C60.9890625,75.4458333 61.7109375,75.0005208 62.5,75.0005208 L64.5828125,75.0005208 C81.8140625,75.0005208 95.8328125,60.9817708 95.8328125,43.7505208 C95.8328125,26.5192708 81.8140625,12.5005208 64.5828125,12.5005208 L35.415625,12.5005208 C18.184375,12.5005208 4.165625,26.5192708 4.165625,43.7505208 C4.165625,60.9817708 18.184375,75.0005208 35.415625,75.0005208 L37.5,75.0005208 L37.5,79.1661458 L35.415625,79.1661458 C15.8875,79.1661458 0,63.2786458 0,43.7505208 C0,24.2208333 15.8875,8.33333333 35.415625,8.33333333 L64.5828125,8.33333333 C84.1109375,8.33333333 100,24.2208333 100,43.7505208 C100,63.2786458 84.1109375,79.1661458 64.5828125,79.1661458 L63.7875,79.1661458 L61.45625,83.8286458 C57.753125,91.2333333 50.3109375,95.8333333 42.03125,95.8333333 L42.03125,95.8333333 Z"
                ></path>
            </svg>
        </button>

        <form
            v-if="stage === 'select'"
            @submit.prevent="() => {}"
            :class="formClasses"
        >
            <button
                class="absolute top-2 right-4 text-gray-600 text-sm hover:underline"
                @click="selectStage('start')"
            >
                Cancel
            </button>

            <label
                class="font-bold mb-4 mt-3"
                for="service-select"
            >
                Live Chat: Please select your service
            </label>

            <select
                id="service-select"
                v-model="serviceType"
                class="w-full mb-4"
            >
                <option :value="null">Select Your Service</option>
                <option
                    v-for="(type, i) in serviceTypes"
                    :key="i"
                    :value="type.value"
                    v-text="type.key"
                />
            </select>

            <button
                :disabled="serviceType === null"
                class="btn-primary-sm ml-auto"
                @click="selectStage('chat')"
            >
                Next
            </button>
        </form>
    </div>
</template>

<script>
import buildForm from './form'
import buildConfig from './config'

const GENESYS_URL = 'https://apps.mypurecloud.com/widgets/9.0/cxbus.min.js'

export default {
    components: {},
    props: {
        isBuyflow: {
            required: true,
            type: Boolean,
        },
        autoInvite: {
            required: true,
            type: Boolean,
        },
        buttonType: {
            required: true,
            type: String,
            validate(value) {
                if (!['business', 'residential'].includes(value)) {
                    return false
                }

                return true
            },
        },
        envKey: {
            type: String,
            default() {
                let host = window.location.host

                if (host === 'www.breezeline.com') {
                    return '5331abc7-9ef9-4e8d-9675-0cde36bba21b'
                }

                return 'fa02eff0-6496-4358-82cb-51c5de337013'
            },
        },
        /**
         * Pass in an array with a single value to skip the form selection and
         * have the component automatically select the single value and open up
         * the chat.
         **/
        serviceTypes: {
            required: false,
            default() {
                return [
                    {
                        key: 'Breezeline Internet, TV or Home Phone',
                        value: 'landline',
                    },
                    {
                        key: 'Breezeline Mobile',
                        value: 'mobile',
                    },
                ]
            },
        },
    },
    data() {
        return {
            serviceType: null,
            stage: 'start',
            availableStages: ['start', 'select', 'chat', 'completed'],
            chatService: {
                isLoading: false,
                init: false,
                plugin: null,
                status: null,
            },
        }
    },
    computed: {
        formType() {
            if (this.serviceType === 'mobile') {
                return 'mobile'
            }

            if (this.serviceType === 'landline') {
                return this.buttonType
            }

            return null
        },
        form() {
            return { formJSON: buildForm(this.formType) }
        },
        buttonClasses() {
            let classes = ['custom-chat-button']

            if (this.isBuyflow) {
                classes.push([
                    'buyflow-button-alt',
                    'buyflow-custom-chat-button',
                    'ml-4',
                ])
            }

            return classes
        },
        formClasses() {
            let classes = [
                'p-4',
                'bg-white',
                'shadow-rev',
                'rounded-md',
                'flex',
                'flex-wrap',
                'w-full',
                'desktop:w-80',
                'z-50',
            ]

            if (this.isBuyflow) {
                classes.push([
                    'fixed',
                    'desktop:absolute',
                    'left-0',
                    'desktop:left-4',
                    'desktop:bottom-0',
                ])
            } else {
                classes.push(['fixed', 'bottom-4', 'right-4'])
            }

            return classes
        },
        wrapperClasses() {
            let classes = ['chatWrapper', 'relative']

            if (this.isBuyflow) {
                classes.push(['--is-buyflow'])
            }

            return classes
        },
    },
    methods: {
        selectStage(stage) {
            if (!this.availableStages.includes(stage)) {
                throw new Error(`Invalid stage: ${stage}`)
            }

            // if the stage we're going to is select but we're already on
            // select then go back to start.
            if (stage === 'select' && this.stage === 'select') {
                stage = 'start'
            }

            if (stage === 'select' && this.serviceTypes.length === 1) {
                this.serviceType = this.serviceTypes[0].value
                stage = 'chat'
            }

            if (stage === 'select' && this.chatService.status === 'opened') {
                stage = 'chat'
            }

            if (
                stage === 'select' &&
                this.formType !== null &&
                this.chatService.status === 'minimized'
            ) {
                stage = 'chat'
            }

            if (stage === 'chat') {
                this.toggleChat()
            }

            this.stage = stage
        },
        toggleChat() {
            if (
                this.chatService.init === true &&
                this.chatService.status === 'minimized'
            ) {
                this.chatService.plugin.command('WebChat.minimize')
                return
            }

            if (
                this.chatService.init === true &&
                this.chatService.status === 'opened'
            ) {
                this.chatService.plugin.command('WebChat.minimize')
                return
            }

            if (this.chatService.init === false) {
                this.initChatService()
                return
            }

            this.chatService.plugin
                .command('WebChat.open', this.form)
                .done((e) => {})
                .fail((e) => {})
        },
        initChatService() {
            this.chatService.isLoading = true

            // init genesys widget
            let script = document.createElement('script')
            let config = buildConfig(this.envKey, this.autoInvite)

            config.form = this.form

            script.setAttribute('src', GENESYS_URL)
            script.setAttribute('ref', 'genesysScript')

            config.onReady = () => {
                this.chatService.plugin = CXBus.registerPlugin('Custom')

                this.chatService.plugin.subscribe('WebChat.closed', () => {
                    this.selectStage('start')
                    this.chatService.status = 'closed'
                })

                this.chatService.plugin.subscribe('WebChat.cancelled', () => {
                    this.selectStage('start')
                    this.chatService.status = 'cancelled'
                })

                this.chatService.plugin.subscribe('WebChat.completed', () => {
                    this.selectStage('start')
                    this.chatService.status = 'completed'
                })

                this.chatService.plugin.subscribe('WebChat.ready', () => {
                    this.chatService.init = true
                    this.chatService.status = 'ready'
                })

                this.chatService.plugin.subscribe('WebChat.minimized', (e) => {
                    this.chatService.status = 'minimized'
                })

                this.chatService.plugin.subscribe(
                    'WebChat.unminimized',
                    () => {}
                )

                this.chatService.plugin.subscribe('WebChat.opened', () => {
                    this.chatService.isLoading = false
                    this.chatService.status = 'opened'
                    this.addGAEvents()
                })

                this.toggleChat()
            }

            script.onload = () => {
                if (!window._genesys) {
                    window._genesys = {}
                }

                if (!window._gt) {
                    window._gt = []
                }

                window._genesys.widgets = config

                CXBus.configure({
                    debug: false,
                    pluginsPath:
                        'https://apps.mypurecloud.com/widgets/9.0/plugins/',
                })

                CXBus.loadPlugin('widgets-core')
            }

            document.body.appendChild(script)
        },
        addGAEvents() {
            const classExists =
                document.getElementsByClassName('cx-submit').length > 0

            if (classExists) {
                document
                    .querySelector('.cx-submit')
                    .setAttribute(
                        'class',
                        'cx-submit cx-btn cx-btn-primary i18n track'
                    )
                document
                    .querySelector('.cx-submit')
                    .setAttribute('data-category', 'start chat')
                document
                    .querySelector('.cx-submit')
                    .setAttribute('data-action', window.location.href)
                document
                    .querySelector('.cx-submit')
                    .setAttribute('data-label', 'live chat')
            }

            const elements = document.getElementsByClassName('track')

            const track = function () {
                const category = this.getAttribute('data-category') || ''
                const action = this.getAttribute('data-action') || ''
                const label = this.getAttribute('data-label') || ''
                const value = this.getAttribute('data-value') || 0

                if (category || action || label) {
                    gtag('event', 'click', {
                        event_category: category,
                        event_action: action,
                        event_label: label,
                        event_value: value,
                    })
                }
            }

            Array.from(elements).forEach(function (element) {
                element.addEventListener('click', track)
            })
        },
        closeGenesys() {},

        chatIconSupport() {
                const chat = document.getElementById('chat');
                
                if(chat) {
                    chat.style.cursor = "pointer";
                    chat.addEventListener('click', function() {
                        const chatBtn = document.querySelector('.custom-chat-button');
                        const chatFormCancel = document.getElementById('service-select');

                        if (chatBtn) {
                            chatBtn.click();
                        }
                        if (chatFormCancel) {
                            chatFormCancel.parentElement.firstChild.click();
                        }

                    });
                }

        },
    },
    mounted() {
        this.chatIconSupport();
    }
}
</script>
